import type { LeadFormInputField, LeadFormVariant } from '@/types/lead-forms';
import {
  areAllCheckboxesChecked,
  areFormConsentsComplete,
} from '@/components/forms/formConsent.utils';
import { isLeadFieldRequired, isLeadNameField, isLeadPhoneField } from '@/lib/lead-forms';

export function isNonEmptyString(value: string | undefined | null): boolean {
  return Boolean(value?.trim());
}

export function isValidEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
}

/**
 * Person names: must start with a letter; allow letters (Latin + Devanagari),
 * spaces, and common punctuation (., ', -). No digits or other symbols.
 */
export const PERSON_NAME_PATTERN =
  /^[\u0900-\u097FA-Za-z\u00C0-\u024F][\u0900-\u097FA-Za-z\u00C0-\u024F\s.'-]*$/;

/** Strip digits and disallowed symbols while typing a person name. */
export function normalizePersonNameInput(value: string, maxLength = 120): string {
  return value.replace(/[^\u0900-\u097FA-Za-z\u00C0-\u024F\s.'-]/g, '').slice(0, maxLength);
}

/** Valid person / "Your Name" field (trimmed length + letters-only pattern). */
export function isValidPersonName(
  value: string,
  options?: { min?: number; max?: number },
): boolean {
  const trimmed = value.trim();
  const min = options?.min ?? 2;
  const max = options?.max ?? 120;
  if (trimmed.length < min || trimmed.length > max) return false;
  return PERSON_NAME_PATTERN.test(trimmed);
}

/** Digits-only Indian mobile, max 10. Strips leading `91` / `0` when pasted. */
export function normalizeIndianMobileInput(value: string): string {
  let digits = value.replace(/\D/g, '');
  if (digits.length >= 12 && digits.startsWith('91')) {
    digits = digits.slice(2);
  } else if (digits.length === 11 && digits.startsWith('0')) {
    digits = digits.slice(1);
  } else if (digits.length > 10) {
    digits = digits.slice(-10);
  }
  return digits.slice(0, 10);
}

/** Valid Indian mobile: 10 digits starting with 6–9. */
export function isValidIndianMobile(value: string): boolean {
  return /^[6-9]\d{9}$/.test(normalizeIndianMobileInput(value));
}

/** Digits-only Indian pincode, max 6. */
export function normalizeIndianPincodeInput(value: string): string {
  return value.replace(/\D/g, '').slice(0, 6);
}

/**
 * Valid Indian PIN code: exactly 6 digits, first digit 1–9
 * (India Post PINs never start with 0).
 */
export function isValidPincode(value: string): boolean {
  return /^[1-9]\d{5}$/.test(normalizeIndianPincodeInput(value));
}

/** Detect CMS / form fields that collect an Indian pincode. */
export function isPincodeField(field: {
  label?: string | null;
  inputId?: string | null;
  placeholder?: string | null;
}): boolean {
  const haystack = [field.label, field.inputId, field.placeholder]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return /pin\s*code|pincode|postal\s*code|zip\s*code|zipcode/.test(haystack);
}

/** Detect free-text / CMS fields meant for Indian state. */
export function isStateField(field: {
  label?: string | null;
  inputId?: string | null;
  placeholder?: string | null;
}): boolean {
  if (isPincodeField(field)) return false;
  const haystack = [field.label, field.inputId, field.placeholder]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return /\bstate\b/.test(haystack) && !/\bdistrict\b/.test(haystack);
}

/** Detect free-text / CMS fields meant for district / city. */
export function isDistrictField(field: {
  label?: string | null;
  inputId?: string | null;
  placeholder?: string | null;
}): boolean {
  if (isPincodeField(field)) return false;
  const haystack = [field.label, field.inputId, field.placeholder]
    .filter(Boolean)
    .join(' ')
    .toLowerCase();
  return /\b(district|dist|tehsil|city)\b/.test(haystack);
}

export function isLeadInputFieldValid(field: LeadFormInputField, value: string): boolean {
  const trimmed = value.trim();
  const required = isLeadFieldRequired(field);

  if (isPincodeField(field)) {
    if (!trimmed) return !required;
    return isValidPincode(trimmed);
  }

  if (required && !trimmed) return false;
  if (!trimmed) return true;

  if (isLeadPhoneField(field)) {
    return isValidIndianMobile(trimmed);
  }

  if (isLeadNameField(field)) {
    return isValidPersonName(trimmed);
  }

  switch (field.inputType) {
    case 'Email':
      return isValidEmail(trimmed);
    case 'Number':
      return /^\d+$/.test(trimmed);
    default:
      if (field.pattern) {
        try {
          return new RegExp(field.pattern).test(trimmed);
        } catch {
          return true;
        }
      }
      return true;
  }
}

const asArray = <T>(value: T[] | undefined | null): T[] => value ?? [];

export interface DynamicLeadFormValidationOptions {
  leadFormNameContact: LeadFormInputField[];
  activeVariant?: LeadFormVariant;
  values: Record<string, string | boolean>;
  prefillName?: string;
  prefillPhone?: string;
  privacyConsent?: boolean;
  marketingConsent?: boolean;
  onPrivacyConsentChange?: (checked: boolean) => void;
  onMarketingConsentChange?: (checked: boolean) => void;
  visibleCmsCheckboxIds?: string[];
  isStandardConsentCheckbox?: (label: string) => boolean;
}

function isStateDistrictComplete(
  variant: LeadFormVariant,
  getString: (key: string) => string,
): boolean {
  for (const field of asArray(variant.stateDistrictDropdown)) {
    // Only block when CMS marks the geo pair required (`required: true`).
    if (!isLeadFieldRequired(field)) continue;
    const stateKey = field.stateFieldId || `state-${field.id}`;
    const districtKey = field.districtFieldId || `district-${field.id}`;

    if (field.stateFieldLabel && !isNonEmptyString(getString(stateKey))) return false;
    if (field.districtFieldLabel && !isNonEmptyString(getString(districtKey))) return false;
  }
  return true;
}

function areVariantSelectsComplete(
  variant: LeadFormVariant,
  getString: (key: string) => string,
): boolean {
  return asArray(variant.selectType).every((field) => {
    if (!isLeadFieldRequired(field)) return true;
    return isNonEmptyString(getString(field.selectId));
  });
}

function areVariantTextAreasComplete(
  variant: LeadFormVariant,
  getString: (key: string) => string,
): boolean {
  return asArray(variant.textArea).every((field) => {
    if (!isLeadFieldRequired(field)) return true;
    return isNonEmptyString(getString(field.textareaId));
  });
}

export function isDynamicLeadFormComplete({
  leadFormNameContact,
  activeVariant,
  values,
  prefillName,
  prefillPhone,
  privacyConsent = false,
  marketingConsent = false,
  onPrivacyConsentChange,
  onMarketingConsentChange,
  visibleCmsCheckboxIds = [],
}: DynamicLeadFormValidationOptions): boolean {
  const getString = (key: string) => {
    const value = values[key];
    return typeof value === 'string' ? value : '';
  };
  const getBool = (key: string) => values[key] === true;

  const nameContactFields = asArray(leadFormNameContact);
  const prefilled = Boolean(prefillName || prefillPhone);
  // When step 1 supplied name/phone, still validate the remaining nameContact rows.
  const nameContactToValidate = prefilled
    ? nameContactFields.filter((field) => !(isLeadNameField(field) || isLeadPhoneField(field)))
    : nameContactFields;

  if (
    nameContactToValidate.length > 0 &&
    !nameContactToValidate.every((field) => isLeadInputFieldValid(field, getString(field.inputId)))
  ) {
    return false;
  }

  if (!activeVariant) return false;

  if (
    !asArray(activeVariant.inputType).every((field) =>
      isLeadInputFieldValid(field, getString(field.inputId)),
    )
  ) {
    return false;
  }

  if (!isStateDistrictComplete(activeVariant, getString)) return false;

  if (!areVariantSelectsComplete(activeVariant, getString)) return false;

  if (!areVariantTextAreasComplete(activeVariant, getString)) return false;

  if (!areAllCheckboxesChecked(visibleCmsCheckboxIds.map((id) => getBool(id)))) {
    return false;
  }

  if (onPrivacyConsentChange) {
    return areFormConsentsComplete({
      privacyConsent,
      marketingConsent,
      // Privacy is mandatory to accept a case; promotional consent is optional
      // (it still maps to CommunicationConsent 1/0 in the API).
      requireMarketing: false,
    });
  }

  return true;
}

export interface ProductsEnquiryFormValues {
  firstName: string;
  lastName: string;
  phoneNumber: string;
  state: string;
  district: string;
  requestType: string;
  message: string;
  privacyConsent: boolean;
  marketingConsent: boolean;
}

export function isProductsEnquiryFormComplete(
  formValues: ProductsEnquiryFormValues,
  options?: {
    stateDistrictEnabled?: boolean;
    requireRequestType?: boolean;
  },
): boolean {
  const stateDistrictEnabled = options?.stateDistrictEnabled !== false;

  if (!isValidPersonName(formValues.firstName)) return false;
  if (!isValidIndianMobile(formValues.phoneNumber)) return false;
  if (stateDistrictEnabled && !isNonEmptyString(formValues.state)) return false;
  if (options?.requireRequestType && !isNonEmptyString(formValues.requestType)) return false;

  return areFormConsentsComplete({
    privacyConsent: formValues.privacyConsent,
    marketingConsent: formValues.marketingConsent,
    requireMarketing: false,
  });
}

export function isAboutFaqFormComplete(values: {
  name: string;
  email: string;
  message: string;
  privacyConsent: boolean;
  marketingConsent: boolean;
  phone?: string;
}): boolean {
  if (!isValidPersonName(values.name)) return false;
  if (!isValidEmail(values.email)) return false;
  if (values.message.trim().length < 10) return false;
  if (values.phone?.trim() && !isValidIndianMobile(values.phone)) return false;

  return areFormConsentsComplete({
    privacyConsent: values.privacyConsent,
    marketingConsent: values.marketingConsent,
    requireMarketing: false,
  });
}

export function isContactFallbackFormComplete(values: {
  email: string;
  subject: string;
  message: string;
  privacyConsent: boolean;
  marketingConsent: boolean;
}): boolean {
  if (!isValidEmail(values.email)) return false;
  if (!isNonEmptyString(values.subject)) return false;
  if (!isNonEmptyString(values.message)) return false;

  return areFormConsentsComplete({
    privacyConsent: values.privacyConsent,
    marketingConsent: values.marketingConsent,
    requireMarketing: false,
  });
}

export function isTradeEnquiryFormComplete(values: {
  email: string;
  message: string;
  privacyConsent: boolean;
}): boolean {
  if (!isValidEmail(values.email)) return false;
  if (values.message.trim().length < 10) return false;

  return areFormConsentsComplete({ privacyConsent: values.privacyConsent });
}
